import React, { useMemo } from 'react';
import { Printer, X, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTenant } from '../contexts/TenantContext';
import RightDrawer from './RightDrawer';

/**
 * Branded payment receipt — printable on success of a reception
 * check-in or pharmacy dispense. Pulls letterhead from tenant branding
 * so every receipt carries the clinic's identity, not generic SaaS
 * chrome.
 *
 * Design intent: a premium A6-style receipt the patient can fold into
 * their wallet. The header is a layered ribbon with the clinic
 * monogram + contact strip; the body sits on a subtle paper texture;
 * the footer carries a signature line, a thank-you, an authenticity
 * mark and the clinic's full address block so this slip stands on its
 * own as a tax-paid stub.
 */
export default function PaymentReceipt({ data, onClose }) {
  const { tenant } = useTenant();
  const b = tenant?.branding || {};
  const name = b.display_name || tenant?.name || 'Our Clinic';
  const primary = b.primary_color || '#0f766e';
  const logo = b.prescription_logo_url || b.logo_url;
  const lhLines = (b.letterhead_text || '').split('\n').map(s => s.trim()).filter(Boolean);
  // First line is treated as the tagline; the rest become the address
  // / contact block in the footer.
  const tagline = lhLines[0] || '';
  const addressLines = lhLines.slice(1);
  const footer = b.prescription_footer_text || '';
  const number = data?.receipt_no ||
    `RCP-${new Date().toISOString().replace(/\D/g, '').slice(0, 14)}`;
  const now = new Date();
  const stamp = now.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' });
  // Build a per-receipt short verification code from the receipt number
  // so the footer "authenticity" stamp feels real, not decorative.
  const verifyCode = useMemo(() => {
    const s = (number || '').replace(/[^A-Z0-9]/gi, '');
    return (s.slice(-8) || 'NEW').toUpperCase();
  }, [number]);

  // Soften / lighten the brand color for surface fills without bringing
  // another library. Hex parsing kept defensive so a malformed brand
  // color falls back to teal.
  const tint = (hex, alpha) => {
    const h = (hex || '').replace('#', '');
    if (h.length !== 6) return `rgba(15, 118, 110, ${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const bl = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${bl}, ${alpha})`;
  };

  // body scroll is locked by RightDrawer; no useEffect needed.

  const doPrint = () => window.print();

  return (
    <RightDrawer
      open={true}
      onClose={onClose}
      title={`Receipt · ${name}`}
      subtitle={`Payment of ₹${Number(data?.amount || 0).toFixed(2)} collected${data?.patient_name ? ' · ' + data.patient_name : ''}`}
      icon={<CheckCircle2 className="h-5 w-5 text-white" />}
      accent={primary}
      widthClass="w-full md:w-[60vw] max-w-[1100px]"
      printableId="print-receipt"
      data-testid="payment-receipt-modal"
      footer={
        <>
          <Button variant="outline" onClick={onClose} data-testid="receipt-close">
            <X className="h-3.5 w-3.5 mr-1" /> Close
          </Button>
          <Button onClick={doPrint} className="text-white"
            style={{ background: primary }} data-testid="receipt-print">
            <Printer className="h-3.5 w-3.5 mr-1" /> Print receipt
          </Button>
        </>
      }
    >
      <style>{`
        @media print {
          @page { margin: 12mm; }
          body * { visibility: hidden; }
          #print-receipt, #print-receipt * { visibility: visible; }
          #print-receipt { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="p-6">
      <div className="bg-white rounded-2xl shadow-md max-w-md mx-auto overflow-hidden ring-1 ring-zinc-100 print:shadow-none print:max-w-full print:rounded-none">
        <div className="receipt-card-inner">
          {/* ============= HEADER ============= */}
          <div className="relative text-white overflow-hidden"
               style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 55%, ${primary}99 100%)` }}>
            {/* Watermark dot pattern */}
            <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
                 style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '14px 14px' }} />
            {/* Decorative oversized ring (top-right) */}
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full border-[12px] border-white/10" />
            {/* Decorative slim ring (bottom-left) */}
            <div className="absolute -bottom-10 -left-10 h-28 w-28 rounded-full border-[6px] border-white/10" />

            <div className="relative px-7 pt-6 pb-5">
              {/* Brand line */}
              <div className="flex items-center gap-3">
                {logo ? (
                  <img src={logo} alt="logo"
                       className="h-14 w-14 rounded-xl bg-white p-1.5 object-contain shadow-md ring-2 ring-white/60" />
                ) : (
                  <div className="h-14 w-14 rounded-xl bg-white/95 flex items-center justify-center font-extrabold text-2xl shadow-md ring-2 ring-white/60"
                       style={{ color: primary, fontFamily: 'Manrope' }}>
                    {name[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-extrabold text-[20px] leading-tight tracking-tight truncate"
                     style={{ fontFamily: 'Manrope' }}>
                    {name}
                  </p>
                  {tagline && (
                    <p className="text-[11px] uppercase tracking-[0.18em] opacity-90 truncate mt-0.5">
                      {tagline}
                    </p>
                  )}
                </div>
              </div>

              {/* Document title divider */}
              <div className="mt-5 mb-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-white/30" />
                <p className="text-[10px] uppercase tracking-[0.35em] font-semibold opacity-95">
                  Official Payment Receipt
                </p>
                <div className="h-px flex-1 bg-white/30" />
              </div>

              {/* Amount + Receipt # row */}
              <div className="flex items-end justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest opacity-80">Amount paid</p>
                  <p className="text-3xl font-extrabold tracking-tight leading-none mt-0.5"
                     style={{ fontFamily: 'Manrope' }}>
                    ₹{Number(data?.amount || 0).toFixed(2)}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] uppercase tracking-widest opacity-80">Receipt #</p>
                  <p className="font-mono text-[12px] font-semibold mt-0.5 bg-white/15 px-2 py-1 rounded-md border border-white/20">
                    {number}
                  </p>
                </div>
              </div>
            </div>

            {/* Notch / perforation strip — visual cue this is a tear-off
                stub. Rendered as a row of light circles cut out of the
                bottom edge of the header. */}
            <div className="relative h-3 -mb-px">
              <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 flex items-center justify-between">
                {Array.from({ length: 22 }).map((_, i) => (
                  <span key={i} className="block h-1.5 w-1.5 rounded-full bg-white/95" />
                ))}
              </div>
            </div>
          </div>

          {/* ============= BODY ============= */}
          <div className="px-7 pt-5 pb-3 space-y-2.5"
               style={{ background: `linear-gradient(180deg, ${tint(primary, 0.04)} 0%, white 60%)` }}>
            <Row label="Date & time" value={stamp} />
            <Row label="Patient" value={data?.patient_name || '—'} emphasis />
            {data?.queue_no && <Row label="Queue number" value={`#${data.queue_no}`} />}
            <Row label="For" value={data?.purpose || 'Consultation'} />
            {data?.doctor_name && <Row label="Doctor" value={data.doctor_name} />}
            <Row label="Payment method" value={(data?.method || 'cash').toUpperCase()} />
            {data?.reference && <Row label="Reference" value={data.reference} mono />}
            {data?.collected_by && <Row label="Collected by" value={data.collected_by} />}
          </div>

          <div className="px-7 pb-4">
            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg border text-sm"
                 style={{ background: tint(primary, 0.08), borderColor: tint(primary, 0.25), color: primary }}>
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span className="font-semibold">Payment received in full</span>
              <span className="ml-auto text-[11px] opacity-70 font-medium">Thank you for visiting</span>
            </div>
          </div>

          {/* ============= FOOTER ============= */}
          <div className="relative">
            {/* Top perforation matching the header bottom */}
            <div className="relative h-3 -mt-px" aria-hidden>
              <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 flex items-center justify-between">
                {Array.from({ length: 22 }).map((_, i) => (
                  <span key={i} className="block h-1.5 w-1.5 rounded-full" style={{ background: tint(primary, 0.18) }} />
                ))}
              </div>
            </div>

            <div className="px-7 pt-3 pb-5" style={{ background: tint(primary, 0.04) }}>
              {/* Signature + Authenticity stamp */}
              <div className="grid grid-cols-5 gap-4 items-end mb-4">
                <div className="col-span-3">
                  <div className="border-b border-zinc-400/50 h-7" />
                  <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                    Authorised signature
                  </p>
                </div>
                <div className="col-span-2 text-right">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border-2 border-dashed"
                       style={{ borderColor: tint(primary, 0.4), color: primary }}>
                    <ShieldCheck className="h-3.5 w-3.5" />
                    <span className="text-[10px] font-bold tracking-wider">VERIFIED</span>
                  </div>
                  <p className="font-mono text-[9px] text-zinc-500 mt-1 tracking-wider">
                    #{verifyCode}
                  </p>
                </div>
              </div>

              {/* Clinic address / contact block */}
              {(addressLines.length > 0 || footer) && (
                <div className="border-t pt-3" style={{ borderColor: tint(primary, 0.2) }}>
                  {addressLines.length > 0 && (
                    <p className="text-[11px] text-zinc-700 leading-relaxed font-medium text-center"
                       style={{ fontFamily: 'Manrope' }}>
                      {addressLines.join(' · ')}
                    </p>
                  )}
                  {footer && (
                    <p className="text-[10px] text-zinc-500 italic mt-2 whitespace-pre-wrap text-center leading-relaxed">
                      {footer}
                    </p>
                  )}
                </div>
              )}

              {/* Legal / fine print */}
              <p className="text-[9px] text-zinc-400 text-center mt-3 leading-relaxed">
                This is a computer-generated receipt and is valid without a physical signature.
                Please retain it for your records. For queries about this payment, quote receipt
                <span className="font-mono"> {number}</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </RightDrawer>
  );
}

function Row({ label, value, mono, emphasis }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-zinc-500 text-[11px] uppercase tracking-wider">{label}</span>
      <span className={`text-right break-words ${emphasis ? 'font-bold text-zinc-900' : 'font-semibold text-zinc-800'} ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </span>
    </div>
  );
}
